import {
  type Announcements,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  type TouchSensorOptions,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  isolationPointDropMove,
  isolationPointId,
} from 'features/lotoReports/lotoIsolationPoints'
import type { LotoIsolationPoint } from 'features/lotoReports/lotoReportSchemas'
import { GripVertical } from 'lucide-react'
import {
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import styled from 'styled-components'

type ReorderAccessibility = {
  announcements: Announcements
  screenReaderInstructions: { draggable: string }
}

type Sortable = ReturnType<typeof useSortable>

type SortableHandle = Pick<Sortable, 'attributes' | 'setActivatorNodeRef'> &
  Partial<Pick<Sortable, 'listeners'>>

type LotoDragActivator = 'handle' | 'row'

type LotoSortLayout = 'list' | 'slots'

const layoutToStrategy = {
  list: verticalListSortingStrategy,
  slots: rectSortingStrategy,
}

const keyboardOptions = { coordinateGetter: sortableKeyboardCoordinates }

const rowMouseOptions = { activationConstraint: { distance: 5 } }

const rowTouchOptions: TouchSensorOptions = {
  activationConstraint: { delay: 250, tolerance: 5 },
  bypassActivationConstraint: ({ event, activeNode }) =>
    containsTarget(activeNode.activatorNode.current, event.target),
}

function containsTarget(
  element: Element | null,
  target: EventTarget | null
): boolean {
  return element !== null && target instanceof Node && element.contains(target)
}

function useActivatorSensors(
  activator: LotoDragActivator
): ReturnType<typeof useSensors> {
  const pointer = useSensor(PointerSensor)
  const mouse = useSensor(MouseSensor, rowMouseOptions)
  const touch = useSensor(TouchSensor, rowTouchOptions)
  const keyboard = useSensor(KeyboardSensor, keyboardOptions)
  const rowSensors = useSensors(mouse, touch, keyboard)
  const handleSensors = useSensors(pointer, keyboard)
  return activator === 'row' ? rowSensors : handleSensors
}

type RowListeners = NonNullable<Sortable['listeners']>

type LotoSortablePoint = {
  setNodeRef: (element: HTMLElement | null) => void
  transform: string | undefined
  transition: string | undefined
  isDragging: boolean
  handle: SortableHandle
}

const DragHandleSlot = styled.div`
  display: flex;
  touch-action: none;
  cursor: grab;

  & > button {
    cursor: inherit;
  }

  & > button[aria-pressed='true'] {
    cursor: grabbing;
  }
`

function useReorderAccessibility(
  points: LotoIsolationPoint[],
  ids: string[]
): ReorderAccessibility {
  const { t } = useLingui()
  const lastOverId = useRef<UniqueIdentifier | undefined>(undefined)
  return useMemo(() => {
    const count = points.length
    const tagOf = (id: UniqueIdentifier): string =>
      points[ids.indexOf(String(id))]?.tag ?? ''
    const positionOf = (id: UniqueIdentifier): number =>
      ids.indexOf(String(id)) + 1
    const returned = (id: UniqueIdentifier): string => {
      const tag = tagOf(id)
      const position = positionOf(id)
      return t`Reordering cancelled. ${tag} is back at position ${position} of ${count}.`
    }
    return {
      announcements: {
        onDragStart: ({ active }) => {
          lastOverId.current = active.id
          const tag = tagOf(active.id)
          const position = positionOf(active.id)
          return t`Picked up ${tag} at position ${position} of ${count}.`
        },
        onDragOver: ({ active, over }) => {
          if (!over || over.id === lastOverId.current) return undefined
          lastOverId.current = over.id
          const tag = tagOf(active.id)
          const position = positionOf(over.id)
          return t`${tag} moved to position ${position} of ${count}.`
        },
        onDragEnd: ({ active, over }) => {
          if (!over) return returned(active.id)
          const tag = tagOf(active.id)
          const position = positionOf(over.id)
          return t`${tag} dropped at position ${position} of ${count}.`
        },
        onDragCancel: ({ active }) => returned(active.id),
      },
      screenReaderInstructions: {
        draggable: t`To reorder an isolation point, press Space or Enter on its drag handle, move it with the up and down arrow keys, then press Space or Enter to drop it or Escape to cancel.`,
      },
    }
  }, [ids, points, t])
}

export function LotoSortablePoints({
  points,
  activator,
  layout,
  onMove,
  children,
}: {
  points: LotoIsolationPoint[]
  activator: LotoDragActivator
  layout: LotoSortLayout
  onMove: (from: number, to: number) => void
  children: ReactNode
}): ReactElement {
  const ids = useMemo(() => points.map(isolationPointId), [points])
  const accessibility = useReorderAccessibility(points, ids)
  const sensors = useActivatorSensors(activator)
  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const move = isolationPointDropMove(
        ids,
        String(active.id),
        over ? String(over.id) : undefined
      )
      if (move) onMove(move.from, move.to)
    },
    [ids, onMove]
  )
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={accessibility}
      onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={layoutToStrategy[layout]}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

export function useLotoSortablePoint(
  point: LotoIsolationPoint,
  axis: 'free' | 'vertical'
): LotoSortablePoint {
  const { t } = useLingui()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: isolationPointId(point),
    attributes: {
      roleDescription: t({
        message: 'sortable',
        context: 'Drag handle role description',
      }),
    },
  })
  const movement =
    transform && axis === 'vertical' ? { ...transform, x: 0 } : transform
  return {
    setNodeRef,
    transform: CSS.Translate.toString(movement) ?? undefined,
    transition,
    isDragging,
    handle: { attributes, listeners, setActivatorNodeRef },
  }
}

function withinRow(listeners: RowListeners): RowListeners {
  return Object.fromEntries(
    Object.entries(listeners).map(([name, listener]) => [
      name,
      (event: SyntheticEvent<HTMLElement>) => {
        if (containsTarget(event.currentTarget, event.target)) listener(event)
      },
    ])
  )
}

export function useLotoSortableRow(
  point: LotoIsolationPoint
): LotoSortablePoint & { rowListeners: RowListeners | undefined } {
  const sortable = useLotoSortablePoint(point, 'vertical')
  const { listeners, ...handle } = sortable.handle
  const rowListeners = useMemo(
    () => listeners && withinRow(listeners),
    [listeners]
  )
  return { ...sortable, handle, rowListeners }
}

export function LotoPointDragHandle({
  tag,
  handle,
}: {
  tag: string
  handle: SortableHandle
}): ReactElement {
  const { t } = useLingui()
  const { attributes, listeners, setActivatorNodeRef } = handle
  return (
    <DragHandleSlot>
      <Button
        ref={setActivatorNodeRef}
        variant='ghost'
        size='icon-sm'
        aria-label={t`Reorder ${tag}`}
        {...attributes}
        {...listeners}>
        <GripVertical />
      </Button>
    </DragHandleSlot>
  )
}
